/**
 * On-demand loader for the in-house chat SDK (@privacybee/chat-sdk).
 *
 * The SDK renders no launcher of its own until `openChat()` runs — `autoAttach()`
 * only installs a click delegate for `.pb-contact-support` triggers — so a caller
 * owns the button. `PbChatLauncher` is that caller in every app that mounts one.
 *
 * Bound to a host once per app (see `ChatWidgetHost`): the loader's in-flight
 * promise lives in that binding's closure, so repeated calls anywhere in one app
 * share a single fetch of the ~85KB bundle.
 */
import type {
    ChatSdk,
    ChatWidget,
    ChatWidgetEnvironment,
    ChatWidgetHost,
} from './types';
import { SDK_HOST_ID, chatWidgetWindow, tearDownSdkWidget } from './sdk';

/**
 * The key the hand-off flag is shared under. Chat opens from places that are not
 * the launcher — a "Contact Support" button on a settings page, for one — and
 * every facade in the app has to stand down for those too.
 */
export const SDK_OWNS_LAUNCHER_KEY = 'pb-chat-sdk-owns-launcher';

/**
 * localStorage prefixes the SDK stamps conversation state under (socket.ts).
 * Their presence is the only signal available BEFORE the bundle loads that this
 * browser is already in a conversation.
 *
 * BOTH matter. The cursor only appears once a `msg.recv` echo has come back, so
 * a first-time chatter who sends a message and then reloads — or loses
 * connectivity — has an outbox entry and no cursor. Checking the cursor alone
 * strands exactly the person with an undelivered message: nothing reconnects,
 * so nothing drains the queue.
 */
const CHAT_STATE_PREFIXES = ['pb-chat-cursor:', 'pb-chat-outbox:'];

/**
 * The anonymous identity the SDK mints on first use (identity.ts). Unlike
 * localStorage it is scoped to `.privacybee.com`, so it survives the trip from
 * one Privacy Bee origin to another — but only while the visitor is anonymous:
 * a customer connect clears it. `pb_chat_seen` covers the signed-in case.
 */
const ANON_COOKIE = 'pb_anon_id';
const UUIDV4 = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/** Bind the widget to one app. Called once, at the app's chat composable. */
export function defineChatWidget(host: ChatWidgetHost): ChatWidget {
    let sdkPromise: Promise<ChatSdk> | null = null;

    function useSdkOwnsLauncher() {
        return host.sharedFlag(SDK_OWNS_LAUNCHER_KEY);
    }

    /**
     * Which backend this build should talk to.
     *
     * The SDK's baked-in defaults are PRODUCTION, so every non-production build
     * has to override them explicitly. A deployed dev build is not a dev
     * server, so keying off `host.dev` alone would point it at the live chat
     * backend — hence the runtime-config arm.
     */
    function chatWidgetEnvironment(): ChatWidgetEnvironment {
        const { chatWsUrl, chatApiBase, authCookieSuffix, authApp } = host.config();

        return {
            // NOTE /chat/v2/ — /chat/v1/ is a frozen bundle the publish workflow
            // never writes, so pinning to it would silently serve stale code.
            // A dev server serves the locally-built bundle from public/ (see
            // scripts/build-chat-sdk-local.sh) so SDK changes are testable
            // without a CDN publish.
            sdkSrc: host.dev ? '/chat-sdk/sdk.js' : 'https://cdn.privacybee.com/chat/v2/sdk.js',
            wsUrl: host.dev ? 'wss://ws.privacybee.test' : (chatWsUrl ?? null),
            apiBase: host.dev ? 'https://api.privacybee.test' : (chatApiBase ?? null),
            // Without the suffix the SDK reads `auth._token.pb` — production's
            // cookie — from a staging or dev build and connects the widget under
            // the wrong identity, because every tier shares the .privacybee.com
            // cookie jar.
            authCookieSuffix: authCookieSuffix ?? '',
            authBase: authApp ?? null,
        };
    }

    /**
     * Load and initialise the SDK, resolving its global. Idempotent — repeated
     * calls share one in-flight load. Rejects if the bundle cannot be fetched,
     * so a caller can say so rather than leaving a button that does nothing.
     *
     * Identity is deliberately not passed in: the SDK resolves it from the
     * `auth._token.pb` cookie (logged-in customer) or `pb_anon_id` (visitor).
     */
    function loadChatWidget(): Promise<ChatSdk> {
        const win = chatWidgetWindow();
        if (win === null) {
            return Promise.reject(new Error('the chat widget is browser-only'));
        }
        if (win.PrivacyBee) {
            return Promise.resolve(win.PrivacyBee);
        }
        if (sdkPromise) {
            return sdkPromise;
        }

        const env = chatWidgetEnvironment();

        // The SDK reads window.PrivacyBeeConfig at module init, so this must be
        // set BEFORE the bundle loads.
        win.PrivacyBeeConfig = Object.assign(
            {},
            win.PrivacyBeeConfig,
            { authCookieSuffix: env.authCookieSuffix, nativeHelpDesk: 'auto' },
            env.wsUrl ? { wsUrl: env.wsUrl } : {},
            env.apiBase ? { apiBase: env.apiBase } : {},
            env.authBase ? { authBase: env.authBase } : {},
        );

        // Resolved here, synchronously: the load callback runs outside any app
        // instance, where the shared flag cannot be reached.
        const sdkOwnsLauncher = useSdkOwnsLauncher();

        sdkPromise = new Promise((resolve, reject) => {
            const script = document.createElement('script');
            script.src = env.sdkSrc;
            script.async = true;
            script.addEventListener('load', () => {
                // A bundle that loaded and defined nothing is, to every caller
                // here, an SDK with no methods — each one already asks whether
                // the member it wants is a function, and says so when it is not.
                const sdk = win.PrivacyBee ?? {};
                // The SDK tears its own widget down when the session that owned
                // it ends in another tab. Nothing else tells this app, so the
                // facade would go on standing down for a launcher that is no
                // longer there, leaving no way into support until a full reload.
                sdk?.onEvent?.('chat.reset', () => { sdkOwnsLauncher.value = false; });
                resolve(sdk);
            });
            script.addEventListener('error', () => {
                sdkPromise = null; // allow a retry
                reject(new Error(`chat sdk failed to load from ${env.sdkSrc}`));
            });
            document.head.appendChild(script);
        });

        return sdkPromise;
    }

    /**
     * THE way to open support chat: load the SDK, open the panel, and hand it
     * the corner — but only once it has actually taken it. The SDK refuses to
     * initialise on an origin outside the `.privacybee.com` / `.privacybee.test`
     * family (identity.ts), and `openChat()` swallows that: it warns and returns
     * without building anything. Flipping the flag regardless would retire the
     * only chat button on the page while opening nothing, which is exactly what
     * a developer on a bare localhost host would hit. The host element is the
     * proof.
     *
     * Returns whether the SDK is now on screen, so a caller that reports status
     * can say so.
     *
     * The shared flag is resolved BEFORE the await — it needs the app instance,
     * which an await can lose.
     */
    async function openSupportChat(): Promise<boolean> {
        const sdkOwnsLauncher = useSdkOwnsLauncher();
        const sdk = await loadChatWidget();
        if (typeof sdk?.openChat !== 'function') {
            throw new Error('the SDK loaded but exposes no openChat()');
        }
        sdk.openChat();

        const mounted = typeof document !== 'undefined'
            && (sdk.isSupportMounted?.() ?? document.getElementById(SDK_HOST_ID) !== null);
        sdkOwnsLauncher.value = mounted;

        return mounted;
    }

    /**
     * Has this browser already been in a conversation?
     *
     * Two signals, because neither covers the other. localStorage carries the
     * real proof — a cursor or a queued message — but it is per-ORIGIN, so a
     * visitor who chatted on privacybee.com and followed a CTA here arrives with
     * none of it. The anon cookie crosses that boundary.
     *
     * The cookie is the weaker claim: it is minted when the widget first boots,
     * so it can outlive a visitor who opened chat and never typed. Connecting
     * for them costs a socket and nothing else — and it buys the case that
     * matters, because the anonymous→customer merge runs on the first customer
     * connect carrying that hint. Waiting for a manual click defers their old
     * thread and their unread replies indefinitely.
     */
    function hasExistingConversation(): boolean {
        if (typeof localStorage !== 'undefined') {
            try {
                for (let i = 0; i < localStorage.length; i += 1) {
                    const key = localStorage.key(i);
                    if (key && CHAT_STATE_PREFIXES.some((prefix) => key.startsWith(prefix))) return true;
                }
            } catch {
                // Storage disabled (private mode, blocked cookies) — fall
                // through to the cookie rather than breaking the page.
            }
        }
        if (typeof document === 'undefined') return false;

        // Set by the SDK on every message, on ANY identity, and scoped to
        // `.privacybee.com` — the one signal that survives both the origin hop
        // and a customer connect, which clears pb_anon_id. Suffixed per
        // environment for the same reason the Passport cookie is: every tier
        // shares that domain.
        const { authCookieSuffix } = host.config();
        if (new RegExp(`(?:^|; )pb_chat_seen${authCookieSuffix ?? ''}=1`).test(document.cookie)) return true;

        const match = document.cookie.match(new RegExp(`(?:^|; )${ANON_COOKIE}=([^;]*)`));

        return match !== null && UUIDV4.test(decodeURIComponent(match[1] ?? ''));
    }

    /**
     * Put an existing conversation back on the wire without opening the panel.
     *
     * Loading the SDK only on click means there is no socket after a reload or a
     * navigation, so a reply that lands while the member is on another page
     * reaches nothing — no resume, no unread state, and the SDK's auto-open on a
     * fresh operator reply cannot fire. This runs only for a browser that has
     * actually chatted, so a first-time visitor still pays no bundle.
     */
    async function restoreSupportChat(): Promise<boolean> {
        const sdkOwnsLauncher = useSdkOwnsLauncher();
        const sdk = await loadChatWidget();
        if (typeof sdk?.restoreChat !== 'function') {
            // The published bundle predates restoreChat. Degrading is correct —
            // openChat() would throw an uninvited panel at every page load — but
            // it must not be silent: nobody gets a socket until they click, and
            // this is the line that says why. Publish chat-sdk
            // (chat-sdk-publish.yaml).
            console.warn('[PrivacyBee] chat SDK has no restoreChat(); publish chat-sdk to restore sessions');

            return false;
        }

        const restored = sdk.restoreChat() === true;
        sdkOwnsLauncher.value = restored;

        return restored;
    }

    /** {@see tearDownSdkWidget}, plus handing the corner back to the facade. */
    function resetSupportChat(): void {
        tearDownSdkWidget();
        useSdkOwnsLauncher().value = false;
    }

    return {
        chatWidgetEnvironment,
        loadChatWidget,
        useSdkOwnsLauncher,
        openSupportChat,
        hasExistingConversation,
        restoreSupportChat,
        resetSupportChat,
    };
}
